import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ColorPicker from '@groovehq/internal-design-system/lib/components/ColorPicker/ColorPicker'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import headerImg from '@groovehq/internal-design-system/lib/assets/images/folder-mail.png'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { doSaveChannelDraft, doClearChannelDraft } from 'ducks/channels/actions'
import CONFIG from './config'
import { styles } from './styles'

const ChannelDetails = ({
  onClose,
  drawerChannelType: channelType = 'forwarding',
  drawerResourceId: channelId,
  drawerId,
}) => {
  const dispatch = useDispatch()
  const {
    schema: FORM_SCHEMA,
    showEmail,
    drawer,
    drawerTitle,
    title,
    intro,
    formEmptyState,
  } = CONFIG[channelType]

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    drawer
  )

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: { ...formEmptyState, id: channelId },
  })

  const handleOnClose = useCallback(
    () => {
      dispatch(doClearChannelDraft(channelId))
      reset({ ...formEmptyState, id: channelId })
      onClose()
    },
    [dispatch, onClose, reset, channelId, formEmptyState]
  )

  const onSubmit = useCallback(
    data => {
      dispatch(doSaveChannelDraft(channelId, data))
      openNextDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            nextDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [dispatch, openNextDrawer, nextDrawerId, channelId, channelType]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const formId = `${drawerId}-form`

  const footer = (
    <ModalBtns
      saveBtnText="Next step"
      saveBtnHtmlType="submit"
      saveBtnDisabled={!isValid}
      saveBtnForm={formId}
      tertiaryBtnText="Back"
      onClickTertiaryBtn={handleBack}
    />
  )

  const { onChange, name: colorName } = register('color')
  const colorPickerDefault = watch('color', formEmptyState.color)

  const handleColorChange = useCallback(
    colorValue => {
      onChange({
        target: {
          name: colorName,
          value: colorValue,
        },
      })
    },
    [onChange, colorName]
  )

  return (
    <Drawer title={drawerTitle} footer={footer} onClose={handleOnClose}>
      <HeaderWithImage
        className="grui mb-12 mt-10"
        imgWidth="120px"
        css={styles.header}
        img={headerImg}
        alt="Envelope"
        title={title}
        intro={intro}
      />
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <div>
          {showEmail && (
            <Field
              label="Mailbox email address"
              placeholder="Enter email address..."
              tooltipTitle="This is the email address your reply from"
              tooltipPosition="right"
              className="grui mt-12"
              css={styles.fullWidth}
              validateStatus={errors?.email ? 'error' : undefined}
              help={errors?.email?.message}
              {...register('email')}
            />
          )}
          <Field
            label="Mailbox name"
            placeholder="Enter name here..."
            tooltipTitle="This is the name displayed in your inbox"
            tooltipPosition="right"
            className="grui mt-12"
            css={styles.fullWidth}
            validateStatus={errors?.name ? 'error' : undefined}
            help={errors?.name?.message}
            {...register('name')}
          />
          <div
            css={[fieldStyles.labelBox, styles.colorLabel]}
            className="grui mt-12"
          >
            Mailbox color
          </div>
          <ColorPicker
            defaultColor={colorPickerDefault}
            pickerBtnStyles={styles.colorPickerBtn}
            css={styles.popoverContainer}
            onColorChange={handleColorChange}
          />
        </div>
      </form>
    </Drawer>
  )
}

export default ChannelDetails
